import { Injectable } from '@angular/core';
import { CartItemModel, CreateOrderCourseSelectionModel, OrderItemModel, OrderModel } from '../../core/models';
import { clusterNormalTotal, clusterSaving, cartBookingItems } from '../../core/cluster-pricing';

// Invoice data model used when downloading an invoice from cart, booking confirmation or order list.
export interface InvoiceDownloadModel {
  // Invoice or booking reference.
  reference: string;
  // Student/customer name.
  customerName: string;
  // Student/customer email.
  customerEmail: string;
  // Student/customer phone number.
  customerPhone?: string;
  // Payment status.
  paymentStatus: string;
  // Invoice date.
  invoiceDate: string;
  // Course items.
  items: OrderItemModel[];
  // Subtotal amount.
  subtotal: number;
  // GST amount.
  gst: number;
  // Total amount.
  total: number;
  // Currency code.
  currencyCode: string;
  // Selected training date range.
  dateRange?: string;
  // Selected training time.
  time?: string;
  // Selected training location.
  locationName?: string;
  // Selected delivery mode.
  deliveryMode?: string;
}

// Creates a simple downloadable HTML invoice without requiring extra PDF libraries.
@Injectable({ providedIn: 'root' })
export class InvoiceService {
  // Build invoice items from cart items.
  buildItemsFromCart(cartItems: CartItemModel[], selectedDate?: any): OrderItemModel[] {
    // Convert cart course data into invoice line items.
    return cartBookingItems(cartItems).map(item => ({
      courseId: item.course.courseId,
      isCluster: !!item.course.isCluster,
      clusterCourses: item.course.clusterCourses,
      normalTotal: item.course.isCluster ? clusterNormalTotal(item.course) : undefined,
      saving: item.course.isCluster ? clusterSaving(item.course) : undefined,
      courseCode: item.course.courseCode,
      courseName: item.course.courseName,
      imageUrl: item.course.imageUrl,
      quantity: item.quantity || 1,
      unitPrice: Number(item.course.price || 0),
      lineAmount: Number(item.course.price || 0) * Number(item.quantity || 1),
      dateRange: selectedDate?.dateRange || '',
      time: selectedDate?.time || '',
      locationName: selectedDate?.location || item.course.locationName || '',
      deliveryMode: selectedDate?.delivery || item.course.deliveryMode || ''
    }));
  }

  // Build invoice data from an order response.
  buildFromOrder(order: OrderModel): InvoiceDownloadModel {
    // Use order items when API returns them, otherwise create a single summary line.
    const items = order.items?.length ? order.items : [{
      courseId: 0,
      courseName: 'Course booking',
      quantity: 1,
      unitPrice: Number(order.totalAmount || 0),
      lineAmount: Number(order.totalAmount || 0),
      dateRange: order.dateRange || '',
      time: order.time || '',
      locationName: order.locationName || '',
      deliveryMode: order.deliveryMode || ''
    }];

    // Calculate subtotal from item lines when possible.
    const subtotal = items.reduce((total, item) => total + Number(item.lineAmount || 0), 0);

    // Return invoice data.
    return {
      reference: order.orderNo || `ECB-${order.orderId}`,
      customerName: order.customerName || 'Customer',
      customerEmail: order.customerEmail || '',
      customerPhone: order.customerMobileNo || '',
      paymentStatus: order.paymentStatus || 'Pending',
      invoiceDate: order.createdDate || new Date().toISOString(),
      items,
      subtotal: order.subtotalAmount ?? (subtotal || Number(order.totalAmount || 0)),
      gst: order.gstAmount ?? ((subtotal || Number(order.totalAmount || 0)) * 0.10),
      total: order.subtotalAmount != null ? Number(order.totalAmount) : (subtotal || Number(order.totalAmount || 0)) * 1.10,
      currencyCode: order.currencyCode || 'AUD',
      dateRange: order.dateRange || items[0]?.dateRange || '',
      time: order.time || items[0]?.time || '',
      locationName: order.locationName || items[0]?.locationName || '',
      deliveryMode: order.deliveryMode || items[0]?.deliveryMode || ''
    };
  }

  // Download an invoice as an HTML file that opens cleanly in browser and can be printed as PDF.
  downloadInvoice(invoice: InvoiceDownloadModel): void {
    // Create safe file name.
    const fileName = `${(invoice.reference || 'invoice').replace(/[^a-zA-Z0-9-]/g, '-')}-invoice.html`;

    // Build invoice HTML.
    const html = this.createInvoiceHtml(invoice);

    // Create blob file.
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });

    // Create temporary object URL.
    const url = URL.createObjectURL(blob);

    // Create temporary anchor.
    const link = document.createElement('a');

    // Set download URL.
    link.href = url;

    // Set download file name.
    link.download = fileName;

    // Trigger download.
    link.click();

    // Release object URL.
    URL.revokeObjectURL(url);
  }

  // Create invoice HTML body.
  private createInvoiceHtml(invoice: InvoiceDownloadModel): string {

    // Convert money values.
    const money = (value: number) =>
      `${invoice.currencyCode || 'AUD'} ${Number(value || 0).toFixed(2)}`;

    // Create course rows.
    const rows = invoice.items.map(item => `
    <tr>
      <td>
        <strong>${this.escape(item.courseName)}</strong><br>
        <small>${this.escape(item.courseCode || '')}</small>

        ${item.isCluster ? '' : this.sessionDetails(item)}

        ${item.isCluster ? `
          <div class="cluster-box">

            <div class="cluster-title">
              Training cluster
            </div>

            ${(item.clusterCourses || []).map(unit => `
              <div class="cluster-course">
                <strong>
                  ${this.escape(unit.courseCode)}
                  ${this.escape(unit.courseName)}
                </strong>

                <span>
                  ${money(unit.price)}
                </span>

                ${this.sessionDetails(
      item.courseSelections?.find(
        selection => selection.courseId === unit.courseId
      )
    )}
              </div>
            `).join('')}

            <div class="cluster-summary">
              <div>
                <span>Normal total</span>
                <strong>${money(item.normalTotal || 0)}</strong>
              </div>

              <div>
                <span>Cluster price</span>
                <strong>${money(item.unitPrice)}</strong>
              </div>

              <div>
                <span>You save</span>
                <strong>${money(item.saving || 0)}</strong>
              </div>

              <small>
                Excluding GST
              </small>
            </div>

          </div>
        ` : ''}

      </td>

      <td class="center">
        ${Number(item.quantity || 1)}
      </td>

      <td class="right">
        ${money(item.unitPrice)}
      </td>

      <td class="right strong">
        ${money(item.lineAmount)}
      </td>

    </tr>
  `).join('');


    return `
<!doctype html>

<html>

<head>

<meta charset="utf-8">

<meta
  name="viewport"
  content="width=device-width, initial-scale=1.0">

<title>
  ${this.escape(invoice.reference)} Invoice
</title>


<style>

  * {
    box-sizing: border-box;
  }

  body {
    margin: 0;
    padding: 0;
    background: #fffaf2;
    font-family: Arial, Helvetica, sans-serif;
    color: #56302b;
  }


  /* ============================================================
     MAIN DOCUMENT
     ============================================================ */

  .page {
    max-width: 900px;
    margin: 28px auto;
    background: #ffffff;
    border: 1px solid #f2d7a8;
    border-radius: 10px;
    overflow: hidden;
  }


  /* ============================================================
     HEADER
     ============================================================ */

  .header {
    background: #5b3826;
    border-bottom: 4px solid #e97800;
    padding: 30px 34px;
    color: #ffffff;
  }

  .header-inner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
  }

  .brand-name {
    margin: 0;
    font-size: 25px;
    line-height: 32px;
    font-weight: 700;
    color: #ffffff;
  }

  .brand-subtitle {
    margin-top: 7px;
    font-size: 12px;
    line-height: 18px;
    color: #f4a15d;
  }

  .invoice-heading {
    text-align: right;
  }

  .invoice-heading h1 {
    margin: 0;
    font-size: 29px;
    line-height: 36px;
    font-weight: 700;
    color: #ffffff;
  }

  .invoice-reference {
    margin-top: 7px;
    font-size: 13px;
    color: #ffffff;
  }


  /* ============================================================
     CONTENT
     ============================================================ */

  .content {
    padding: 30px 34px 34px 34px;
  }


  /* ============================================================
     REFERENCE / STATUS
     ============================================================ */

  .reference-box {
    background: #fff7e4;
    border: 1px solid #f4dfab;
    border-left: 4px solid #e97800;
    border-radius: 4px;
    padding: 18px 20px;
    margin-bottom: 22px;
  }

  .reference-label {
    font-size: 11px;
    line-height: 17px;
    font-weight: 700;
    color: #8c1b19;
    text-transform: uppercase;
  }

  .reference-value {
    margin-top: 6px;
    font-size: 21px;
    line-height: 28px;
    font-weight: 700;
    color: #56302b;
  }

  .status {
    display: inline-block;
    margin-top: 10px;
    padding: 6px 12px;
    background: #fbd79f;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 700;
    color: #8c1b19;
  }


  /* ============================================================
     DETAILS
     ============================================================ */

  .details-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 24px;
  }

  .info-box {
    border: 1px solid #eaded8;
    background: #ffffff;
  }

  .info-title {
    background: #fff7e4;
    border-bottom: 1px solid #eaded8;
    padding: 12px 14px;
    font-size: 13px;
    line-height: 19px;
    font-weight: 700;
    color: #56302b;
  }

  .info-body {
    padding: 14px;
    font-size: 12px;
    line-height: 20px;
    color: #6f554c;
  }

  .info-body strong {
    color: #56302b;
  }


  /* ============================================================
     SECTION TITLE
     ============================================================ */

  .section-title {
    margin: 26px 0 12px 0;
    font-size: 17px;
    line-height: 23px;
    font-weight: 700;
    color: #56302b;
  }


  /* ============================================================
     COURSE TABLE
     ============================================================ */

  table {
    width: 100%;
    border-collapse: collapse;
  }

  .course-table {
    border: 1px solid #eaded8;
  }

  .course-table th {
    background: #5b3826;
    color: #ffffff;
    text-align: left;
    padding: 12px 14px;
    font-size: 11px;
    line-height: 17px;
    font-weight: 700;
  }

  .course-table th.right {
    text-align: right;
  }

  .course-table th.center {
    text-align: center;
  }

  .course-table td {
    padding: 14px;
    border-bottom: 1px solid #eaded8;
    vertical-align: top;
    font-size: 12px;
    line-height: 19px;
    color: #56302b;
  }

  .course-table tr:last-child td {
    border-bottom: none;
  }

  .course-table small {
    display: inline-block;
    margin-top: 4px;
    color: #806c65;
    font-size: 10px;
  }

  .center {
    text-align: center;
  }

  .right {
    text-align: right;
  }

  .strong {
    font-weight: 700;
  }


  /* ============================================================
     SESSION DETAILS
     ============================================================ */

  .muted {
    margin-top: 6px;
    color: #806c65;
    font-size: 10px;
    line-height: 17px;
  }

  .muted strong {
    color: #56302b;
  }


  /* ============================================================
     CLUSTER DETAILS
     ============================================================ */

  .cluster-box {
    margin-top: 12px;
    background: #fff7e4;
    border-left: 3px solid #e97800;
    padding: 12px;
  }

  .cluster-title {
    margin-bottom: 8px;
    color: #8c1b19;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
  }

  .cluster-course {
    padding: 8px 0;
    border-bottom: 1px solid #efd9bd;
    font-size: 11px;
  }

  .cluster-course:last-of-type {
    border-bottom: none;
  }

  .cluster-course > span {
    display: block;
    margin-top: 3px;
    color: #6f554c;
  }

  .cluster-summary {
    margin-top: 10px;
    border-top: 1px solid #e7c996;
    padding-top: 9px;
  }

  .cluster-summary > div {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    padding: 3px 0;
    font-size: 10px;
  }

  .cluster-summary small {
    margin-top: 6px;
  }


  /* ============================================================
     TOTALS
     ============================================================ */

  .totals {
    width: 340px;
    margin: 22px 0 0 auto;
  }

  .total-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 0;
    font-size: 12px;
    color: #6f554c;
  }

  .total-row strong {
    color: #56302b;
  }

  .grand-total {
    margin-top: 7px;
    padding-top: 12px;
    border-top: 2px solid #e97800;
    color: #56302b;
    font-size: 18px;
    font-weight: 700;
  }

  .grand-total span:last-child {
    color: #e97800;
  }


  /* ============================================================
     NOTICE
     ============================================================ */

  .notice {
    margin-top: 26px;
    background: #fff7e4;
    border-left: 3px solid #e97800;
    padding: 15px 16px;
    font-size: 11px;
    line-height: 18px;
    color: #56302b;
  }


  /* ============================================================
     FOOTER
     ============================================================ */

  .footer {
    margin-top: 28px;
    background: #5b3826;
    color: #ffffff;
    text-align: center;
    padding: 18px 20px;
  }

  .footer-title {
    font-size: 12px;
    line-height: 18px;
    font-weight: 700;
  }

  .footer-details {
    margin-top: 5px;
    font-size: 10px;
    line-height: 17px;
    color: #ffffff;
  }


  /* ============================================================
     MOBILE
     ============================================================ */

  @media (max-width: 700px) {

    .page {
      margin: 0;
      border-radius: 0;
    }

    .header-inner {
      display: block;
    }

    .invoice-heading {
      margin-top: 18px;
      text-align: left;
    }

    .details-grid {
      grid-template-columns: 1fr;
    }

    .content {
      padding: 24px 18px;
    }

    .totals {
      width: 100%;
    }

  }


  /* ============================================================
     PRINT
     ============================================================ */

  @media print {

    body {
      background: #ffffff;
    }

    .page {
      margin: 0;
      max-width: none;
      border: none;
      border-radius: 0;
    }

  }

</style>

</head>


<body>

<div class="page">


  <!-- ========================================================= -->
  <!-- HEADER -->
  <!-- ========================================================= -->

  <div class="header">

    <div class="header-inner">

      <div>

        <h1 class="brand-name">
          ECB Training
        </h1>

        <div class="brand-subtitle">
          Training booking invoice
        </div>

      </div>


      <div class="invoice-heading">

        <h1>
          INVOICE
        </h1>

        <div class="invoice-reference">
          ${this.escape(invoice.reference)}
        </div>

      </div>

    </div>

  </div>


  <!-- ========================================================= -->
  <!-- CONTENT -->
  <!-- ========================================================= -->

  <div class="content">


    <!-- REFERENCE -->
    <div class="reference-box">

      <div class="reference-label">
        Booking Reference
      </div>

      <div class="reference-value">
        ${this.escape(invoice.reference)}
      </div>

      <span class="status">
        ${this.escape(invoice.paymentStatus)}
      </span>

    </div>


    <!-- DETAILS -->
    <div class="details-grid">


      <!-- STUDENT DETAILS -->
      <div class="info-box">

        <div class="info-title">
          Student Details
        </div>

        <div class="info-body">

          <strong>Name:</strong>
          ${this.escape(invoice.customerName)}
          <br>

          <strong>Email:</strong>
          ${this.escape(invoice.customerEmail)}
          <br>

          ${invoice.customerPhone
        ? `
                <strong>Phone:</strong>
                ${this.escape(invoice.customerPhone)}
                <br>
              `
        : ''
      }

          <strong>Invoice Date:</strong>
          ${this.escape(invoice.invoiceDate)}

        </div>

      </div>


      <!-- TRAINING DETAILS -->
      <div class="info-box">

        <div class="info-title">
          Training Details
        </div>

        <div class="info-body">

          ${invoice.items.some(
        item => item.courseSelections?.length
      )

        ? `
              Dates, times and locations are listed
              under each course below.
            `

        : `
              ${this.optionalLine(
          'Date',
          invoice.dateRange
        )}

              ${this.optionalLine(
          'Time',
          invoice.time
        )}

              ${this.optionalLine(
          'Location',
          invoice.locationName
        )}

              ${this.optionalLine(
          'Delivery',
          invoice.deliveryMode
        )}
            `
      }

        </div>

      </div>

    </div>


    <!-- ======================================================= -->
    <!-- COURSE SUMMARY -->
    <!-- ======================================================= -->

    <div class="section-title">
      Booking Summary
    </div>


    <table class="course-table">

      <thead>

        <tr>

          <th>
            Course
          </th>

          <th class="center">
            Qty
          </th>

          <th class="right">
            Unit Price
          </th>

          <th class="right">
            Line Total
          </th>

        </tr>

      </thead>


      <tbody>

        ${rows}

      </tbody>

    </table>


    <!-- ======================================================= -->
    <!-- TOTALS -->
    <!-- ======================================================= -->

    <div class="totals">

      <div class="total-row">

        <span>
          Subtotal
        </span>

        <strong>
          ${money(invoice.subtotal)}
        </strong>

      </div>


      <div class="total-row">

        <span>
          GST (10%)
        </span>

        <strong>
          ${money(invoice.gst)}
        </strong>

      </div>


      <div class="total-row grand-total">

        <span>
          Total
        </span>

        <span>
          ${money(invoice.total)}
        </span>

      </div>

    </div>


    <!-- ======================================================= -->
    <!-- NOTICE -->
    <!-- ======================================================= -->

    <div class="notice">

      Please keep this invoice for your records.
      If you have any questions regarding your booking,
      contact ECB Training.

    </div>


  </div>


  <!-- ========================================================= -->
  <!-- FOOTER -->
  <!-- ========================================================= -->

  <div class="footer">

    <div class="footer-title">
      ECB Training
    </div>

    <div class="footer-details">
      1300 ECB 256
      &nbsp; | &nbsp;
      info@ecbtraining.com.au
    </div>

  </div>


</div>

</body>

</html>
`;
  }

  // Render optional line only when value exists.
  private sessionDetails(session?: CreateOrderCourseSelectionModel): string {
    if (!session) return '';
    return `<div class="muted" style="margin:4px 0 10px">${this.optionalLine('Date', session.dateRange)}${this.optionalLine('Time', session.time)}${this.optionalLine('Location', session.locationName)}${this.optionalLine('Delivery', session.deliveryMode)}</div>`;
  }

  private optionalLine(label: string, value?: string): string {
    // Return empty string when missing.
    return value ? `<strong>${label}:</strong> ${this.escape(value)}<br>` : '';
  }

  // Basic HTML escape.
  private escape(value: string): string {
    // Replace unsafe characters.
    return String(value || '').replace(/[&<>'"]/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[char] || char));
  }
}
